import type { PlayerHudEntry } from '@aod/ui';
// The offers still waiting on you, and the last few things that happened.
//
// The toasts fade after ~10 s so they stop covering the board; this is where
// an offer stays answerable. It reads the same list the toasts do
// (`pendingOffers`), so an offer the proposer withdraws, the sim lets lapse,
// or a finished match clears leaves here in the same snapshot.
import { Button } from '@aod/ui/components/ui/button';
import type { PendingOffer } from '../../dynasty/pendingOffers.js';
import type { Notice } from '../../dynasty/useTreatyNews.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { houseName } from '../../sea/houseNames.js';

export interface OfferAnswers {
  readonly onTreaty?: (from: number, accept: boolean) => void;
  readonly onMarriage?: (from: number, accept: boolean) => void;
  readonly onMerchant?: (cityId: string, accept: boolean) => void;
}

export function PendingOffers({
  offers,
  players,
  answers,
}: {
  readonly offers: readonly PendingOffer[];
  readonly players: readonly PlayerHudEntry[];
  readonly answers: OfferAnswers;
}) {
  const t = useT();
  if (offers.length === 0) return null;
  const nameOf = (seat: number) => players.find((p) => p.playerId === seat)?.name ?? `#${seat + 1}`;
  return (
    <section
      className="flex flex-col gap-1.5 rounded-md border border-emerald-500/40 bg-card/60 px-2.5 py-2"
      aria-label={t('treaty.offers.title') as string}
      data-testid="pending-offers"
      {...appDom({ id: 'pending-offers', type: 'Card', scope: 'section' })}
    >
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t('treaty.offers.title')}
      </h3>
      {offers.map((o) => {
        const answer = (accept: boolean) => {
          if (o.kind === 'treaty') answers.onTreaty?.(o.from, accept);
          else if (o.kind === 'marriage') answers.onMarriage?.(o.from, accept);
          else answers.onMerchant?.(o.merchant.cityId, accept);
        };
        const label =
          o.kind === 'treaty'
            ? t('treaty.offerTitle', { house: nameOf(o.from), treaty: t(`treaty.of.${o.state}`) })
            : o.kind === 'marriage'
              ? t('dynasty.offerTitle', { house: nameOf(o.from) })
              : `${t('sea.offerTitle')} — ${t(houseName(o.merchant.offer.house))}`;
        return (
          <div
            key={o.key}
            className="flex flex-wrap items-center gap-1.5 text-xs"
            data-testid={`pending-offer-${o.kind}`}
          >
            <span className="min-w-0 flex-1 text-[11px] leading-snug text-foreground">{label}</span>
            <Button
              type="button"
              size="sm"
              className="h-6 px-2 text-[11px]"
              aria-label={t('treaty.accept.for', { what: label }) as string}
              onClick={() => answer(true)}
            >
              {t('treaty.accept')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-6 px-2 text-[11px]"
              aria-label={t('treaty.decline.for', { what: label }) as string}
              onClick={() => answer(false)}
            >
              {t('treaty.decline')}
            </Button>
          </div>
        );
      })}
    </section>
  );
}

export function RecentTreatyNews({
  history,
  nameOf,
}: {
  readonly history: readonly Notice[];
  readonly nameOf: (seat: number) => string;
}) {
  const t = useT();
  if (history.length === 0) return null;
  return (
    <section className="flex flex-col gap-1" data-testid="recent-treaty-news">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t('treaty.recent.title')}
      </h3>
      <ul className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
        {history.map((n) => (
          <li key={n.id}>
            {t(`treaty.news.${n.kind}`, {
              house: nameOf(n.other),
              treaty: t(`treaty.of.${n.state}`),
            })}
          </li>
        ))}
      </ul>
    </section>
  );
}
